import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { userManagementApi } from '../../lib/api';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { ScrollArea } from '../../components/ui/scroll-area';
import { toast } from 'sonner';
import {
  Users, Plus, Search, Edit, Trash2, Key, Shield, ShieldCheck,
  UserCheck, UserX, Loader2, Building2, Mail, UserCog,
  CheckSquare, Square, Clock, Copy, ExternalLink
} from 'lucide-react';

const ROLE_LABELS = {
  super_admin: 'Super Admin', practice_admin: 'Practice Admin', physician: 'Physician',
  nurse: 'Nurse', care_coordinator: 'Care Coordinator', aide: 'Aide (Home Care)',
  billing_specialist: 'Billing Specialist', patient: 'Patient',
  director_patient_services: 'Director of Patient Services',
  service_provider: 'Service Provider',
};
const ROLE_COLORS = {
  super_admin: '#7F1D1D', practice_admin: '#1A56DB', physician: '#0E7490',
  nurse: '#166534', care_coordinator: '#0E7490', aide: '#9A3412',
  billing_specialist: '#92400E', patient: '#64748B',
  director_patient_services: '#7C3AED',
  service_provider: '#0891B2',
};

function formatLastLogin(ts) {
  if (!ts) return 'Never';
  try {
    const d = new Date(ts);
    const now = new Date();
    const diff = Math.floor((now - d) / 1000);
    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
    return d.toLocaleDateString();
  } catch { return 'Unknown'; }
}

export default function UserManagementPage() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [orgs, setOrgs] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [showEdit, setShowEdit] = useState(null);
  const [showDelete, setShowDelete] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', password: '', role: 'physician', organization_id: '' });
  const [saving, setSaving] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkAction, setBulkAction] = useState('');
  const [bulkRole, setBulkRole] = useState('physician');
  const [showBulkConfirm, setShowBulkConfirm] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (roleFilter !== 'all') params.role = roleFilter;
      if (search) params.search = search;
      const [usersRes, orgsRes] = await Promise.all([
        userManagementApi.list(params),
        api.get('/organizations')
      ]);
      setUsers(usersRes.data.users || []);
      // /api/organizations returns a bare list
      const orgsList = Array.isArray(orgsRes.data) ? orgsRes.data : (orgsRes.data.organizations || []);
      setOrgs(orgsList);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [roleFilter]);

  // Auto-fill organization when practice admin has exactly one org (hide the picker in dialogs)
  useEffect(() => {
    if (orgs.length === 1 && !form.organization_id) {
      setForm((f) => ({ ...f, organization_id: orgs[0].id }));
    }
  }, [orgs]);

  const handleSearch = () => load();

  const handleCreate = async () => {
    if (!form.name || !form.email || !form.password || !form.organization_id) {
      toast.error('Fill all required fields'); return;
    }
    setSaving(true);
    try {
      await userManagementApi.create(form);
      toast.success('User created');
      setShowCreate(false);
      setForm({ name: '', email: '', password: '', role: 'physician', organization_id: '' });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to create user'); }
    finally { setSaving(false); }
  };

  const handleUpdate = async () => {
    if (!showEdit) return;
    setSaving(true);
    try {
      const updateData = {};
      if (form.name) updateData.name = form.name;
      if (form.email) updateData.email = form.email;
      if (form.role) updateData.role = form.role;
      if (form.organization_id) updateData.organization_id = form.organization_id;
      await userManagementApi.update(showEdit, updateData);
      toast.success('User updated');
      setShowEdit(null);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update'); }
    finally { setSaving(false); }
  };

  const handleDuplicate = (u) => {
    // Pre-fill Create User dialog from an existing user: same role + org, blank identity.
    setForm({
      name: '',
      email: '',
      password: '',
      role: u.role,
      organization_id: u.organization_id || (orgs.length === 1 ? orgs[0].id : ''),
    });
    setShowCreate(true);
    toast.info(`Copying role/org from ${u.name}`);
  };

  const handleDelete = async () => {
    if (!showDelete) return;
    setSaving(true);
    try {
      await userManagementApi.delete(showDelete);
      toast.success('User deleted');
      setShowDelete(null);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete'); }
    finally { setSaving(false); }
  };

  const handleResetPassword = async (userId) => {
    try {
      const res = await userManagementApi.resetPassword(userId);
      toast.success(`Temporary password: ${res.data.temporary_password}`, { duration: 10000 });
    } catch (err) { toast.error('Failed to reset password'); }
  };

  const handleResetMfa = async (userId, email) => {
    if (!window.confirm(`Reset MFA for ${email}? They'll need to re-enroll on next login.`)) return;
    try {
      await api.post(`/admin/users/${userId}/reset-mfa`);
      toast.success(`MFA reset for ${email}`);
      load();
    } catch { toast.error('Failed to reset MFA'); }
  };

  const handleImpersonate = async (userId) => {
    try {
      const response = await api.post(`/admin/impersonate/${userId}`);
      // Store the new token and original admin ID
      localStorage.setItem('savenes_token', response.data.access_token);
      localStorage.setItem('savenes_original_admin', response.data.original_admin_id);
      localStorage.setItem('savenes_impersonating', response.data.impersonating);
      toast.success(`Now impersonating ${response.data.impersonating}`);
      window.location.href = '/dashboard';
    } catch (err) { toast.error('Failed to impersonate user'); }
  };

  const openEdit = (u) => {
    setForm({ name: u.name, email: u.email, password: '', role: u.role, organization_id: u.organization_id });
    setShowEdit(u.id);
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === users.length) setSelectedIds([]);
    else setSelectedIds(users.map(u => u.id));
  };

  const handleBulkAction = async () => {
    if (!bulkAction || selectedIds.length === 0) return;
    setSaving(true);
    try {
      const payload = { user_ids: selectedIds, action: bulkAction };
      if (bulkAction === 'change_role') payload.role = bulkRole;
      const res = await userManagementApi.bulkAction(payload);
      toast.success(`Bulk action complete: ${res.data.success} succeeded, ${res.data.failed} failed`);
      setSelectedIds([]);
      setBulkAction('');
      setShowBulkConfirm(false);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Bulk action failed'); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-6" data-testid="user-management-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold text-[#0A2342]" style={{ fontFamily: 'Outfit, sans-serif' }}>User Management</h1>
          <p className="text-[#64748B] mt-1">Manage platform users and permissions</p>
        </div>
        <Button onClick={() => { setForm({ name: '', email: '', password: '', role: 'physician', organization_id: orgs.length === 1 ? orgs[0].id : '' }); setShowCreate(true); }} className="careiq-btn-primary" data-testid="create-user-btn">
          <Plus className="w-4 h-4 mr-2" /> New User
        </Button>
      </div>

      {/* Bulk Actions Bar */}
      {selectedIds.length > 0 && (
        <Card className="border-[#0E7490] bg-[#0E7490]/5">
          <CardContent className="p-3 flex items-center gap-4 flex-wrap">
            <span className="text-sm font-medium text-[#0E7490]" data-testid="selected-count">{selectedIds.length} user(s) selected</span>
            <Select value={bulkAction} onValueChange={setBulkAction}>
              <SelectTrigger className="w-44 h-8 text-sm" data-testid="bulk-action-select"><SelectValue placeholder="Bulk Action..." /></SelectTrigger>
              <SelectContent>
                <SelectItem value="delete">Delete Selected</SelectItem>
                <SelectItem value="change_role">Change Role</SelectItem>
                <SelectItem value="deactivate">Deactivate</SelectItem>
                <SelectItem value="activate">Activate</SelectItem>
              </SelectContent>
            </Select>
            {bulkAction === 'change_role' && (
              <Select value={bulkRole} onValueChange={setBulkRole}>
                <SelectTrigger className="w-40 h-8 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(ROLE_LABELS).filter(([k]) => k !== 'super_admin').map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            <Button size="sm" className="bg-[#0E7490] text-white hover:bg-[#0C6580] h-8" onClick={() => bulkAction ? setShowBulkConfirm(true) : toast.error('Select an action')} data-testid="apply-bulk-btn">Apply</Button>
            <Button size="sm" variant="ghost" onClick={() => { setSelectedIds([]); setBulkAction(''); }} className="h-8">Clear</Button>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card className="careiq-card">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
                <Input placeholder="Search by name or email..." value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && handleSearch()} className="careiq-input pl-10" data-testid="user-search" />
              </div>
              <Button variant="outline" onClick={handleSearch}>Search</Button>
            </div>
            <Select value={roleFilter} onValueChange={setRoleFilter}>
              <SelectTrigger className="w-48" data-testid="role-filter">
                <Shield className="w-4 h-4 mr-2" />
                <SelectValue placeholder="All Roles" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Roles</SelectItem>
                {Object.entries(ROLE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Users Table */}
      <Card className="careiq-card">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 text-[#0E7490] animate-spin" /></div>
          ) : users.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[#E2E8F0]">
                    <th className="table-header text-left w-10 pl-4">
                      <button onClick={toggleSelectAll} className="text-[#64748B] hover:text-[#0E7490]" data-testid="select-all-users">
                        {selectedIds.length === users.length && users.length > 0 ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4" />}
                      </button>
                    </th>
                    <th className="table-header text-left">Name</th>
                    <th className="table-header text-left">Email</th>
                    <th className="table-header text-left">Role</th>
                    <th className="table-header text-left">Organization</th>
                    <th className="table-header text-left">Last Login</th>
                    <th className="table-header text-left">Status</th>
                    <th className="table-header text-left">MFA</th>
                    <th className="table-header text-left">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.id} className={`table-row ${selectedIds.includes(u.id) ? 'bg-[#0E7490]/5' : ''}`} data-testid={`user-row-${u.id}`}>
                      <td className="table-cell pl-4">
                        <button onClick={() => toggleSelect(u.id)} className="text-[#64748B] hover:text-[#0E7490]" data-testid={`select-user-${u.id}`}>
                          {selectedIds.includes(u.id) ? <CheckSquare className="w-4 h-4 text-[#0E7490]" /> : <Square className="w-4 h-4" />}
                        </button>
                      </td>
                      <td className="table-cell">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-medium" style={{ backgroundColor: ROLE_COLORS[u.role] || '#64748B' }}>
                            {u.name?.charAt(0) || '?'}
                          </div>
                          <Link to={`/admin/users/${u.id}`} className="font-medium text-[#0A2342] hover:text-[#0E7490] hover:underline" data-testid={`view-user-${u.id}`}>
                            {u.name}
                          </Link>
                        </div>
                      </td>
                      <td className="table-cell text-[#64748B]">{u.email}</td>
                      <td className="table-cell">
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium" style={{ backgroundColor: (ROLE_COLORS[u.role] || '#64748B') + '15', color: ROLE_COLORS[u.role] || '#64748B' }}>
                          {ROLE_LABELS[u.role] || u.role}
                        </span>
                      </td>
                      <td className="table-cell text-[#64748B] text-sm">{u.organization_name || '-'}</td>
                      <td className="table-cell">
                        <span className="flex items-center gap-1 text-xs text-[#64748B]" data-testid={`last-login-${u.id}`}>
                          <Clock className="w-3 h-3" /> {formatLastLogin(u.last_login)}
                        </span>
                      </td>
                      <td className="table-cell">
                        {u.is_active !== false ? (
                          <span className="flex items-center gap-1 text-xs text-[#166534]"><UserCheck className="w-3 h-3" /> Active</span>
                        ) : (
                          <span className="flex items-center gap-1 text-xs text-[#991B1B]"><UserX className="w-3 h-3" /> Inactive</span>
                        )}
                      </td>
                      <td className="table-cell">
                        {u.mfa_enabled ? (
                          <span className="flex items-center gap-1 text-xs text-[#166534]" data-testid={`mfa-on-${u.id}`}><ShieldCheck className="w-3 h-3" /> On</span>
                        ) : (
                          <span className="flex items-center gap-1 text-xs text-[#CA8A04]" data-testid={`mfa-off-${u.id}`}><Shield className="w-3 h-3" /> Off</span>
                        )}
                      </td>
                      <td className="table-cell">
                        <div className="flex items-center gap-1">
                          <Link to={`/admin/users/${u.id}`} title="Open detail" data-testid={`open-detail-${u.id}`}>
                            <Button size="sm" variant="ghost"><ExternalLink className="w-3.5 h-3.5 text-[#0E7490]" /></Button>
                          </Link>
                          {u.role !== 'super_admin' && (
                            <Button size="sm" variant="ghost" className="text-[#0E7490]" onClick={() => handleImpersonate(u.id)} data-testid={`impersonate-${u.id}`}>
                              <UserCog className="w-3.5 h-3.5" />
                            </Button>
                          )}
                          <Button size="sm" variant="ghost" onClick={() => openEdit(u)} data-testid={`edit-user-${u.id}`}><Edit className="w-3.5 h-3.5" /></Button>
                          <Button size="sm" variant="ghost" onClick={() => handleDuplicate(u)} title="Duplicate (copy role & org)" data-testid={`duplicate-user-${u.id}`}><Copy className="w-3.5 h-3.5 text-[#0E7490]" /></Button>
                          <Button size="sm" variant="ghost" onClick={() => handleResetPassword(u.id)} data-testid={`reset-pwd-${u.id}`}><Key className="w-3.5 h-3.5 text-[#92400E]" /></Button>
                          {u.mfa_enabled && (
                            <Button size="sm" variant="ghost" onClick={() => handleResetMfa(u.id, u.email)} data-testid={`reset-mfa-${u.id}`} title="Reset MFA"><Shield className="w-3.5 h-3.5 text-[#991B1B]" /></Button>
                          )}
                          <Button size="sm" variant="ghost" onClick={() => setShowDelete(u.id)} data-testid={`delete-user-${u.id}`}><Trash2 className="w-3.5 h-3.5 text-[#991B1B]" /></Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12 text-[#64748B]">
              <Users className="w-12 h-12 mx-auto mb-3 text-[#94A3B8]" />
              <p>No users found</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create User Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="sm:max-w-md" data-testid="create-user-dialog">
          <DialogHeader><DialogTitle>Create New User</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <div><Label>Full Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-name-input" /></div>
            <div><Label>Email *</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-email-input" /></div>
            <div><Label>Password *</Label><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="user-password-input" /></div>
            <div>
              <Label>Role *</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(ROLE_LABELS).filter(([k]) => k !== 'super_admin').map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {orgs.length === 1 ? (
              <div className="text-xs text-[#64748B] bg-[#F8FAFC] border border-[#E2E8F0] rounded px-3 py-2" data-testid="single-org-note">
                Organization: <b className="text-[#0A2342]">{orgs[0].name}</b>
              </div>
            ) : (
              <div>
                <Label>Organization *</Label>
                <Select value={form.organization_id} onValueChange={(v) => setForm({ ...form, organization_id: v })}>
                  <SelectTrigger data-testid="user-org-select"><SelectValue placeholder="Select organization" /></SelectTrigger>
                  <SelectContent>{orgs.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
              <Button className="careiq-btn-primary" onClick={handleCreate} disabled={saving} data-testid="save-user-btn">{saving ? 'Creating...' : 'Create User'}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={!!showEdit} onOpenChange={() => setShowEdit(null)}>
        <DialogContent className="sm:max-w-md" data-testid="edit-user-dialog">
          <DialogHeader><DialogTitle>Edit User</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <div><Label>Full Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            <div>
              <Label>Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(ROLE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Organization</Label>
              <Select value={form.organization_id} onValueChange={(v) => setForm({ ...form, organization_id: v })}>
                <SelectTrigger><SelectValue placeholder="Select organization" /></SelectTrigger>
                <SelectContent>{orgs.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setShowEdit(null)}>Cancel</Button>
              <Button className="careiq-btn-primary" onClick={handleUpdate} disabled={saving} data-testid="update-user-btn">{saving ? 'Updating...' : 'Update User'}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={!!showDelete} onOpenChange={() => setShowDelete(null)}>
        <DialogContent className="sm:max-w-sm" data-testid="delete-user-dialog">
          <DialogHeader><DialogTitle className="text-[#991B1B]">Delete User</DialogTitle></DialogHeader>
          <p className="text-sm text-[#64748B] mt-2">Are you sure you want to delete this user? This action cannot be undone.</p>
          <div className="flex justify-end gap-3 mt-4">
            <Button variant="outline" onClick={() => setShowDelete(null)}>Cancel</Button>
            <Button className="bg-[#991B1B] hover:bg-[#7F1D1D] text-white" onClick={handleDelete} disabled={saving} data-testid="confirm-delete-btn">{saving ? 'Deleting...' : 'Delete'}</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk Action Confirmation */}
      <Dialog open={showBulkConfirm} onOpenChange={setShowBulkConfirm}>
        <DialogContent className="sm:max-w-sm" data-testid="bulk-confirm-dialog">
          <DialogHeader><DialogTitle>Confirm Bulk Action</DialogTitle></DialogHeader>
          <p className="text-sm text-[#64748B] mt-2">
            Apply <b>{bulkAction?.replace('_', ' ')}</b> to <b>{selectedIds.length}</b> user(s)?
            {bulkAction === 'change_role' && <> New role: <b>{ROLE_LABELS[bulkRole]}</b></>}
          </p>
          <div className="flex justify-end gap-3 mt-4">
            <Button variant="outline" onClick={() => setShowBulkConfirm(false)}>Cancel</Button>
            <Button className="careiq-btn-primary" onClick={handleBulkAction} disabled={saving} data-testid="confirm-bulk-btn">
              {saving ? 'Processing...' : 'Confirm'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
